import { useTranslation } from 'react-i18next';
import AppLayout from '../../../components/AppLayout';
import ReggiePanel from '../../../components/ReggiePanel';
import { canChangeStatus, canModifyClaim } from '../../../utils/rbac';
import CommsTab from './CommsTab';
import NotesTab from './NotesTab';
import AuditTab from './AuditTab';
import AssignModal from './AssignModal';
import EditClaimModal from './EditClaimModal';
import TimelineTab from './TimelineTab';
import DocumentsTab from './DocumentsTab';
import FinancialsTab from './FinancialsTab';
import ChangeStatusModal from './ChangeStatusModal';
import useClaimDetailFunctions from './useClaimDetailFunctions';
import Header from './Header';
import Tabs from './Tabs';

const ClaimDetail = () => {
  const { t } = useTranslation();
  const {
    S,
    history,
    activeTab,
    setActiveTab,
    claim,
    setClaim,
    claimLoading,
    claimError,
    notes,
    setNotes,
    reggieOpen,
    setReggieOpen,
    riskSignals,
    showSignals,
    setShowSignals,
    showStatusModal,
    setShowStatusModal,
    showAssignModal,
    setShowAssignModal,
    showEditModal,
    setShowEditModal,
    timelineEvents,
    fetchClaim,
    userData,
    userRole,
    tabs,
    id,
    SEVERITY_COLORS,
  } = useClaimDetailFunctions();

  if (claimLoading) {
    return (
      <AppLayout>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <div style={{ color: S.textMuted, fontSize: 15 }}>{t('screens.claimsScreen.detail.loading')}</div>
        </div>
      </AppLayout>
    );
  }

  // Only show the error / not-found state when we've never successfully
  // loaded the claim. A late socket refresh or transient 404 must not
  // overwrite a claim that already rendered — that caused T-10.4 (scroll
  // showed both the loaded detail and the "Claim not found" fallback).
  if (!claim) {
    return (
      <AppLayout>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 16 }}>
          <div style={{ color: '#ef4444', fontWeight: 600, fontSize: 16 }}>{claimError || t('screens.claimsScreen.detail.notFound')}</div>
          <button
            onClick={() => {
              fetchClaim();
            }}
            style={{ padding: '10px 24px', background: S.burgundy, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}
          >
            {t('screens.claimsScreen.detail.retry')}
          </button>
          <button
            onClick={() => history.push('/claims')}
            style={{ padding: '8px 20px', background: S.bgCard, border: `1px solid ${S.borderLight}`, borderRadius: 8, cursor: 'pointer', color: S.textSecondary }}
          >
            {t('screens.claimsScreen.detail.backToClaims')}
          </button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>
        {/* Header */}
        <Header
          S={S}
          history={history}
          claim={claim}
          riskSignals={riskSignals}
          SEVERITY_COLORS={SEVERITY_COLORS}
          setShowSignals={setShowSignals}
          userData={userData}
          userRole={userRole}
          reggieOpen={reggieOpen}
          setReggieOpen={setReggieOpen}
          canChangeStatus={canChangeStatus}
          setShowStatusModal={setShowStatusModal}
          canModifyClaim={canModifyClaim}
          setShowAssignModal={setShowAssignModal}
          setShowEditModal={setShowEditModal}
          showSignals={showSignals}
        />

        {/* Tabs */}
        <Tabs tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} S={S} />

        {/* Tab content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px 32px' }}>
          {activeTab === 'timeline' && <TimelineTab events={timelineEvents} S={S} />}
          {activeTab === 'documents' && <DocumentsTab claim={claim} refreshClaim={fetchClaim} S={S} />}
          {activeTab === 'notes' && <NotesTab userRole={userRole} claimId={claim._id} notes={notes} setNotes={setNotes} refreshClaim={fetchClaim} S={S} />}
          {activeTab === 'financials' && <FinancialsTab claim={claim} refreshClaim={fetchClaim} S={S} />}
          {activeTab === 'communications' && <CommsTab claim={claim} claimId={claim._id} notes={notes} setNotes={setNotes} S={S} />}
          {activeTab === 'audit' && <AuditTab claim={claim} S={S} />}
        </div>
      </div>

      <ReggiePanel claimId={id} claimNumber={claim.id} isOpen={reggieOpen} onClose={() => setReggieOpen(false)} />

      {showStatusModal && (
        <ChangeStatusModal
          S={S}
          claim={claim}
          onClose={() => setShowStatusModal(false)}
          onSuccess={newStatus => {
            setClaim(prev => ({ ...prev, status: newStatus }));
            setActiveTab('timeline');
            fetchClaim();
          }}
        />
      )}

      {showAssignModal && (
        <AssignModal
          S={S}
          claim={claim}
          onClose={() => setShowAssignModal(false)}
          onSuccess={() => {
            fetchClaim();
          }}
        />
      )}

      {showEditModal && (
        <EditClaimModal
          S={S}
          claim={claim}
          onClose={() => setShowEditModal(false)}
          onSaved={() => {
            fetchClaim();
          }}
        />
      )}
    </AppLayout>
  );
};

export default ClaimDetail;
